import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Space, Divider, Typography, Avatar, Card } from 'antd';
import {
  ShopOutlined,
  BankOutlined,
  MobileOutlined,
  MailOutlined,
  UserOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { vendorService } from '../../services/vendorService';
import { notification } from 'antd';

const { TextArea } = Input;
const { Title, Text } = Typography;

const VendorForm = ({ vendor, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (vendor) {
      form.setFieldsValue({
        ...vendor,
        bankDetails: (vendor.bankDetails && vendor.bankDetails.length > 0) ? vendor.bankDetails : [{}]
      });
    } else {
      form.resetFields();
      // Ensure one default bank account is visible for new vendors
      form.setFieldsValue({ bankDetails: [{}] });
    }
  }, [vendor, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const data = { ...values };
      Object.keys(data).forEach(key => {
        if (data[key] === '' || data[key] === undefined) {
          data[key] = null;
        }
      });

      if (vendor) {
        await vendorService.updateVendor(vendor.id, data);
        notification.success({
          message: 'Success',
          description: 'Vendor updated successfully'
        });
      } else {
        await vendorService.createVendor(data);
        notification.success({
          message: 'Success',
          description: 'Vendor created successfully'
        });
      }

      onSuccess();
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.error || error.message || 'Failed to save vendor'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      style={{ padding: '0 10px' }}
    >
      <div style={{ marginBottom: 24 }}>
        <Space size="middle">
          <Avatar size="large" icon={<ShopOutlined />} style={{ backgroundColor: '#52c41a' }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {vendor ? 'Edit Vendor Details' : 'Add New Vendor'}
            </Title>
            <Text type="secondary">Manage vendor information and bank details.</Text>
          </div>
        </Space>
      </div>

      <Divider orientation="left"><Space><ShopOutlined />Basic Information</Space></Divider>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            label="Vendor Name"
            name="vendorName"
            rules={[{ required: true, message: 'Please enter vendor name' }]}
          >
            <Input prefix={<ShopOutlined />} placeholder="Company/Vendor Name" size="large" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="GSTN"
            name="gstn"
          >
            <Input placeholder="GST Identification Number" size="large" maxLength={15} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Vendor Address"
            name="vendorAddress"
          >
            <TextArea rows={3} placeholder="Complete address" size="large" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Vendor Mobile"
            name="vendorMobile"
            rules={[{ pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit mobile number' }]}
          >
            <Input prefix={<MobileOutlined />} placeholder="10-digit mobile number" size="large" maxLength={10} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Vendor Email"
            name="vendorEmail"
            rules={[{ type: 'email', message: 'Please enter valid email' }]}
          >
            <Input prefix={<MailOutlined />} placeholder="vendor@example.com" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left"><Space><UserOutlined />Contact Person</Space></Divider>
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            label="Contact Name"
            name="contactName"
          >
            <Input prefix={<UserOutlined />} placeholder="Contact person name" size="large" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Contact Number"
            name="contactNumber"
            rules={[{ pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit mobile number' }]}
          >
            <Input prefix={<MobileOutlined />} placeholder="Contact mobile" size="large" maxLength={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Contact Email"
            name="contactEmail"
            rules={[{ type: 'email', message: 'Please enter valid email' }]}
          >
            <Input prefix={<MailOutlined />} placeholder="contact@example.com" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left"><Space><BankOutlined />Bank Details</Space></Divider>
      <Form.List name="bankDetails">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Card key={key} style={{ marginBottom: 16 }} size="small">
                <Row gutter={16}>
                  <Col span={24}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text strong>Bank Account {key + 1} {key === 0 && <Text type="danger">*</Text>}</Text>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'accountName']}
                      label="Account Name"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
                      ]}
                    >
                      <Input placeholder="Account holder name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'bankName']}
                      label="Bank Name"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input placeholder="Bank name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'branchName']}
                      label="Branch Name"
                    >
                      <Input placeholder="Branch name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'accountNumber']}
                      label="Account Number"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^\d+$/, message: 'Digits only' }
                      ]}
                    >
                      <Input placeholder="Account number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'ifscCode']}
                      label="IFSC Code"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC Format' }
                      ]}
                    >
                      <Input placeholder="IFSC code" style={{ textTransform: 'uppercase' }} maxLength={11} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      {...restField}
                      name={[name, 'remarks']}
                      label="Remarks"
                    >
                      <Input placeholder="Additional notes for this bank account" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
            >
              Add Bank Account
            </Button>
          </>
        )}
      </Form.List>

      <div style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: '100%',
        borderTop: '1px solid #e9e9e9',
        padding: '10px 16px',
        background: '#fff',
        textAlign: 'right',
        zIndex: 1,
      }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading} size="large">
            {vendor ? 'Update' : 'Create'} Vendor
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default VendorForm;

